/* mz-ink, efecto de tinta fluida WebGL2 para el hero de la home de Matcha Zen.
   Solver basado en WebGL-Fluid-Simulation de Pavel Dobryakov (MIT, 2017),
   portado a WebGL2 con dye de 4 canales (color premultiplicado + densidad).
   Colores muestreados de fotos reales de matcha ceremonial:
     espuma    #C9CB9F   esmeralda #526321
   Prefijo unico del modulo: mzink-. No toca ningun selector del sitio. */
(function () {
  'use strict';

  /* ---------- 0. guardas: si algo falta, salida silenciosa sin insertar nada ---------- */

  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var hero = document.querySelector('.mzh-hero');
  if (!hero) return;

  var canvas = document.createElement('canvas');
  var gl = canvas.getContext('webgl2', {
    alpha: true, depth: false, stencil: false,
    antialias: false, premultipliedAlpha: false
  });
  if (!gl || !gl.getExtension('EXT_color_buffer_float')) return;
  var ok = true;
  var halfLinear = !!gl.getExtension('OES_texture_half_float_linear');

  /* ---------- 1. rampa de color y presets ---------- */

  var RAMP = [
    [0.00, [0x52, 0x63, 0x21]],   /* #526321  esmeralda */
    [0.42, [0xc9, 0xcb, 0x9f]],   /* #C9CB9F  espuma    */
    [0.72, [0xc3, 0xe6, 0x5a]],   /* #C3E65A  lima      */
    [1.00, [0x9e, 0xff, 0x65]]    /* #9EFF65  neon      */
  ];
  var NEON = [0x9e / 255, 0xff / 255, 0x65 / 255];

  var PRESET = {
    day:   {dep:.40, dsp:4.0, neon:.92, nfloor:.86, bright:.74, gain:1.40, bub:.15, bsc:300,
            rad:.22, force:1500, curl:9,  dd:1.60, dye:1024, mask:56},
    night: {dep:.52, dsp:5.0, neon:.78, nfloor:.82, bright:1.30, gain:1.60, bub:.22, bsc:340,
            rad:.20, force:1700, curl:11, dd:1.45, dye:1024, mask:52}
  };

  var booted = false, switching = false;
  var mode = document.body.classList.contains('dm-active') ? 'night' : 'day';
  var C = Object.assign({}, PRESET[mode]);
  var manualFilter = true;               /* filtrado SUAVE fijo (bilerp manual) */
  var ramp = 0, rampTarget = 1;

  /* ---------- 2. shaders ---------- */

  var VERT = `#version 300 es
precision highp float;
in vec2 aPos; out vec2 vUv, vL, vR, vT, vB; uniform vec2 texelSize;
void main(){ vUv=aPos*.5+.5;
  vL=vUv-vec2(texelSize.x, 0.); vR=vUv+vec2(texelSize.x, 0.);
  vT=vUv+vec2(0., texelSize.y); vB=vUv-vec2(0., texelSize.y);
  gl_Position=vec4(aPos, 0., 1.); }`;
  var HEAD = `#version 300 es
precision highp float; precision highp sampler2D;
in vec2 vUv, vL, vR, vT, vB; out vec4 o;`;

  function sh(type, src) {
    var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) { console.warn(gl.getShaderInfoLog(s)); ok = false; }
    return s;
  }
  var vs = sh(gl.VERTEX_SHADER, VERT);
  function prog(body, defs) {
    var head = defs ? HEAD.replace('\n', '\n' + defs.map(function (d) { return '#define ' + d; }).join('\n') + '\n') : HEAD;
    var p = gl.createProgram();
    gl.attachShader(p, vs); gl.attachShader(p, sh(gl.FRAGMENT_SHADER, head + '\n' + body));
    gl.bindAttribLocation(p, 0, 'aPos'); gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) { console.warn(gl.getProgramInfoLog(p)); ok = false; }
    var u = {}, n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (var i = 0; i < n; i++) { var nm = gl.getActiveUniform(p, i).name; u[nm] = gl.getUniformLocation(p, nm); }
    return {p: p, u: u};
  }

  var splatP, advP, advMP, curlP, vortP, divP, clearP, presP, gradP, dispP;

  /* el splat escribe 4 canales: rgb = color x peso, a = peso.
     Asi el display recupera el color medio real con rgb/a, y conserva lo oscuro. */
  splatP = prog(`uniform sampler2D uTarget; uniform float aspect; uniform vec3 color;
uniform vec2 point; uniform float radius; uniform float w;
void main(){ vec2 p=vUv-point; p.x*=aspect;
  float g=exp(-dot(p, p)/radius);
  vec4 b=texture(uTarget, vUv);
  o=vec4(b.rgb+g*color, b.a+g*w); }`);

  var ADV = `uniform sampler2D uVelocity; uniform sampler2D uSource;
uniform vec2 texelSize; uniform vec2 dyeTexel; uniform float dt; uniform float diss;
vec4 bilerp(sampler2D sam, vec2 uv, vec2 ts){
  vec2 st=uv/ts-.5; vec2 iuv=floor(st), fuv=fract(st);
  vec4 a=texture(sam, (iuv+vec2(.5, .5))*ts), b=texture(sam, (iuv+vec2(1.5, .5))*ts);
  vec4 c=texture(sam, (iuv+vec2(.5, 1.5))*ts), d=texture(sam, (iuv+vec2(1.5, 1.5))*ts);
  return mix(mix(a, b, fuv.x), mix(c, d, fuv.x), fuv.y); }
void main(){
#ifdef MANUAL
  vec2 co=vUv-dt*bilerp(uVelocity, vUv, texelSize).xy*texelSize;
  vec4 r=bilerp(uSource, co, dyeTexel);
#else
  vec2 co=vUv-dt*texture(uVelocity, vUv).xy*texelSize;
  vec4 r=texture(uSource, co);
#endif
  o=r/(1.+diss*dt); }`;
  advP = prog(ADV); advMP = prog(ADV, ['MANUAL']);

  curlP = prog(`uniform sampler2D uVelocity;
void main(){ float L=texture(uVelocity, vL).y, R=texture(uVelocity, vR).y,
  T=texture(uVelocity, vT).x, B=texture(uVelocity, vB).x;
  o=vec4(.5*((R-L)-(T-B)), 0., 0., 1.); }`);

  vortP = prog(`uniform sampler2D uVelocity; uniform sampler2D uCurl;
uniform float curl; uniform float dt;
void main(){ float L=texture(uCurl, vL).x, R=texture(uCurl, vR).x, T=texture(uCurl, vT).x,
  B=texture(uCurl, vB).x, Cc=texture(uCurl, vUv).x;
  vec2 f=.5*vec2(abs(T)-abs(B), abs(R)-abs(L));
  f/=length(f)+.0001; f*=curl*Cc; f.y=-f.y;
  o=vec4(clamp(texture(uVelocity, vUv).xy+f*dt, -1000., 1000.), 0., 1.); }`);

  divP = prog(`uniform sampler2D uVelocity;
void main(){ float L=texture(uVelocity, vL).x, R=texture(uVelocity, vR).x,
  T=texture(uVelocity, vT).y, B=texture(uVelocity, vB).y;
  vec2 Cc=texture(uVelocity, vUv).xy;
  if(vL.x<0.)L=-Cc.x; if(vR.x>1.)R=-Cc.x; if(vT.y>1.)T=-Cc.y; if(vB.y<0.)B=-Cc.y;
  o=vec4(.5*((R-L)+(T-B)), 0., 0., 1.); }`);

  clearP = prog(`uniform sampler2D uTexture; uniform float value;
void main(){ o=value*texture(uTexture, vUv); }`);

  presP = prog(`uniform sampler2D uPressure; uniform sampler2D uDivergence;
void main(){ float L=texture(uPressure, vL).x, R=texture(uPressure, vR).x,
  T=texture(uPressure, vT).x, B=texture(uPressure, vB).x, d=texture(uDivergence, vUv).x;
  o=vec4((L+R+B+T-d)*.25, 0., 0., 1.); }`);

  gradP = prog(`uniform sampler2D uPressure; uniform sampler2D uVelocity;
void main(){ float L=texture(uPressure, vL).x, R=texture(uPressure, vR).x,
  T=texture(uPressure, vT).x, B=texture(uPressure, vB).x;
  o=vec4(texture(uVelocity, vUv).xy-vec2(R-L, T-B), 0., 1.); }`);

  /* display: color medio real + alfa por densidad + burbujas finas */
  dispP = prog(`uniform sampler2D uTexture; uniform float uGain;
uniform float uBub; uniform float uBsc; uniform float uAspect; uniform float uTime;
float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7)))*43758.5453123); }
float vnoise(vec2 p){
  vec2 i=floor(p), f=fract(p); f=f*f*(3.-2.*f);
  return mix(mix(hash(i), hash(i+vec2(1, 0)), f.x),
             mix(hash(i+vec2(0, 1)), hash(i+vec2(1, 1)), f.x), f.y); }
void main(){
  vec4 c=texture(uTexture, vUv);
  float dens=c.a;
  if(dens<1e-5){ o=vec4(0.); return; }
  vec3 col=c.rgb/max(dens, 1e-5);          /* color medio, conserva claro y oscuro */
  float a=clamp(dens*uGain, 0., 1.);
  if(uBub>.0005){
    vec2 q=vec2(vUv.x*uAspect, vUv.y)*uBsc + vec2(uTime*.012, uTime*-.008);
    float b=vnoise(q)*.62 + vnoise(q*2.31+11.7)*.38;
    b=smoothstep(.56, .93, b);              /* solo las crestas: burbuja, no ruido */
    float lift=1.-smoothstep(.25, .85, a);  /* burbujea en la espuma fina, no en el te espeso */
    col+=b*uBub*(.35+.65*lift);
    a=min(1., a+b*uBub*.55*lift);
  }
  o=vec4(col, a*.999); }`);

  if (!ok) return;   /* fallo de compilacion: nada insertado, nada roto */

  /* ---------- 3. geometria ---------- */

  var vb = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, vb);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]), gl.STATIC_DRAW);
  var ib = gl.createBuffer(); gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ib);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0, 1, 2, 0, 2, 3]), gl.STATIC_DRAW);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0); gl.enableVertexAttribArray(0);

  function blit(t) {
    if (t == null) {
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    } else { gl.viewport(0, 0, t.w, t.h); gl.bindFramebuffer(gl.FRAMEBUFFER, t.fbo); }
    gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
  }
  function fbo(w, h, internal, format) {
    var filt = (manualFilter && halfLinear) ? gl.LINEAR : gl.NEAREST;
    gl.activeTexture(gl.TEXTURE0);
    var t = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filt);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filt);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, format, gl.HALF_FLOAT, null);
    var f = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, f);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
    gl.viewport(0, 0, w, h); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
    return {tex: t, fbo: f, w: w, h: h, tx: 1 / w, ty: 1 / h,
      attach: function (id) { gl.activeTexture(gl.TEXTURE0 + id); gl.bindTexture(gl.TEXTURE_2D, this.tex); return id; }};
  }
  function pair(w, h, internal, format) {
    var a = fbo(w, h, internal, format), b = fbo(w, h, internal, format);
    return {get read() { return a; }, get write() { return b; },
      swap: function () { var t = a; a = b; b = t; }, tx: a.tx, ty: a.ty};
  }

  var SIM = matchMedia('(pointer:coarse)').matches ? 96 : 144;
  var velocity, dye, pressure, divergence, curlFB;
  function res(base) {
    var a = gl.drawingBufferWidth / gl.drawingBufferHeight;
    return a > 1 ? [Math.round(base * a), base] : [base, Math.round(base / a)];
  }
  function freeFBO(t) { if (t) { gl.deleteTexture(t.tex); gl.deleteFramebuffer(t.fbo); } }
  function freePair(p) { if (p) { freeFBO(p.read); freeFBO(p.write); } }
  function initFBOs() {
    freePair(velocity); freePair(dye); freePair(pressure); freeFBO(divergence); freeFBO(curlFB);
    var s = res(SIM), d = res(C.dye);
    velocity = pair(s[0], s[1], gl.RG16F, gl.RG);
    dye = pair(d[0], d[1], gl.RGBA16F, gl.RGBA);
    pressure = pair(s[0], s[1], gl.R16F, gl.RED);
    divergence = fbo(s[0], s[1], gl.R16F, gl.RED);
    curlFB = fbo(s[0], s[1], gl.R16F, gl.RED);
  }
  function size() {
    var dpr = Math.min(devicePixelRatio || 1, 1.5);
    var nw = Math.max(2, Math.round(hero.clientWidth * dpr));
    var nh = Math.max(2, Math.round(hero.clientHeight * dpr));
    if (velocity && nw === canvas.width && Math.abs(nh - canvas.height) < 2) return;
    canvas.width = nw;
    canvas.height = nh;
    initFBOs();
  }

  /* ---------- 4. color: un solo eje, de espuma a esmeralda ---------- */

  function ink(depth) {
    var d = Math.max(0, Math.min(1, depth));
    var i = 0;
    while (i < RAMP.length - 2 && d > RAMP[i + 1][0]) i++;
    var p0 = RAMP[i][0], c0 = RAMP[i][1], p1 = RAMP[i + 1][0], c1 = RAMP[i + 1][1];
    var f = (d - p0) / (p1 - p0);
    f = f * f * (3 - 2 * f);                          /* sin esquinas entre paradas */
    var out = [0, 1, 2].map(function (k) { return (c0[k] + (c1[k] - c0[k]) * f) / 255; });
    /* alino de neon: tira del color entero hacia #9EFF65, mas fuerte cuanto mas arriba */
    var pull = Math.min(1, C.neon * (C.nfloor + (1 - C.nfloor) * d));
    return out.map(function (v, k) { return (v * (1 - pull) + NEON[k] * pull) * C.bright; });
  }

  /* ---------- 5. simulacion ---------- */

  function splat(x, y, dx, dy, color, rad) {
    gl.useProgram(splatP.p);
    gl.uniform1i(splatP.u.uTarget, velocity.read.attach(0));
    gl.uniform1f(splatP.u.aspect, canvas.width / canvas.height);
    gl.uniform2f(splatP.u.point, x, y);
    gl.uniform1f(splatP.u.radius, rad / 100);
    gl.uniform3f(splatP.u.color, dx, dy, 0);
    gl.uniform1f(splatP.u.w, 0);
    blit(velocity.write); velocity.swap();
    gl.useProgram(splatP.p);
    gl.uniform1i(splatP.u.uTarget, dye.read.attach(0));
    gl.uniform3f(splatP.u.color, color[0], color[1], color[2]);
    gl.uniform1f(splatP.u.w, 1);
    blit(dye.write); dye.swap();
  }
  function burst(n, force) {
    for (var i = 0; i < n; i++) {
      var a = Math.random() * 6.283;
      splat(.15 + Math.random() * .7, .2 + Math.random() * .6,
        Math.cos(a) * force, Math.sin(a) * force,
        ink(C.dep + (Math.random() - .5) * .22), C.rad);
    }
  }
  function step(dt) {
    gl.disable(gl.BLEND);
    gl.useProgram(curlP.p);
    gl.uniform2f(curlP.u.texelSize, velocity.tx, velocity.ty);
    gl.uniform1i(curlP.u.uVelocity, velocity.read.attach(0)); blit(curlFB);

    gl.useProgram(vortP.p);
    gl.uniform2f(vortP.u.texelSize, velocity.tx, velocity.ty);
    gl.uniform1i(vortP.u.uVelocity, velocity.read.attach(0));
    gl.uniform1i(vortP.u.uCurl, curlFB.attach(1));
    gl.uniform1f(vortP.u.curl, C.curl); gl.uniform1f(vortP.u.dt, dt);
    blit(velocity.write); velocity.swap();

    gl.useProgram(divP.p);
    gl.uniform2f(divP.u.texelSize, velocity.tx, velocity.ty);
    gl.uniform1i(divP.u.uVelocity, velocity.read.attach(0)); blit(divergence);

    gl.useProgram(clearP.p);
    gl.uniform1i(clearP.u.uTexture, pressure.read.attach(0));
    gl.uniform1f(clearP.u.value, .8); blit(pressure.write); pressure.swap();

    gl.useProgram(presP.p);
    gl.uniform2f(presP.u.texelSize, velocity.tx, velocity.ty);
    gl.uniform1i(presP.u.uDivergence, divergence.attach(0));
    for (var i = 0; i < 20; i++) {
      gl.uniform1i(presP.u.uPressure, pressure.read.attach(1));
      blit(pressure.write); pressure.swap();
    }

    gl.useProgram(gradP.p);
    gl.uniform2f(gradP.u.texelSize, velocity.tx, velocity.ty);
    gl.uniform1i(gradP.u.uPressure, pressure.read.attach(0));
    gl.uniform1i(gradP.u.uVelocity, velocity.read.attach(1));
    blit(velocity.write); velocity.swap();

    var A = advMP;                       /* filtrado suave permanente */
    gl.useProgram(A.p);
    gl.uniform2f(A.u.texelSize, velocity.tx, velocity.ty);
    gl.uniform2f(A.u.dyeTexel, velocity.tx, velocity.ty);
    gl.uniform1i(A.u.uVelocity, velocity.read.attach(0));
    gl.uniform1i(A.u.uSource, velocity.read.attach(0));
    gl.uniform1f(A.u.dt, dt); gl.uniform1f(A.u.diss, .45);
    blit(velocity.write); velocity.swap();

    gl.uniform2f(A.u.dyeTexel, dye.tx, dye.ty);
    gl.uniform1i(A.u.uVelocity, velocity.read.attach(0));
    gl.uniform1i(A.u.uSource, dye.read.attach(1));
    gl.uniform1f(A.u.diss, C.dd);
    blit(dye.write); dye.swap();
  }
  var t0 = performance.now();
  function render() {
    gl.useProgram(dispP.p);
    gl.uniform1i(dispP.u.uTexture, dye.read.attach(0));
    gl.uniform1f(dispP.u.uGain, C.gain);
    gl.uniform1f(dispP.u.uBub, C.bub);
    gl.uniform1f(dispP.u.uBsc, C.bsc);
    gl.uniform1f(dispP.u.uAspect, canvas.width / canvas.height);
    gl.uniform1f(dispP.u.uTime, (performance.now() - t0) / 1000);
    blit(null);
  }

  function applyMask() {
    var w = C.mask;
    if (w <= 0) { canvas.style.maskImage = 'none'; canvas.style.webkitMaskImage = 'none'; return; }
    var m = 'radial-gradient(ellipse ' + w + '% ' + Math.round(w * .78) +
      '% at 50% 46%, transparent 0%, #000 ' + Math.min(99, w + 22) + '%)';
    canvas.style.maskImage = m; canvas.style.webkitMaskImage = m;
  }

  /* ---------- 6. puntero ---------- */

  var P = {x: 0, y: 0, has: false};
  var lastMove = 0, lastAuto = 0;
  addEventListener('pointermove', function (e) {
    if (!booted) return;
    var r = canvas.getBoundingClientRect();
    var x = (e.clientX - r.left) / r.width, y = 1 - (e.clientY - r.top) / r.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) { P.has = false; return; }
    if (P.has) {
      var dx = x - P.x, dy = y - P.y, sp = Math.hypot(dx, dy);
      if (sp > .0001) {
        /* la velocidad es la cuchara: cuanto mas remueves, mas esmeralda sale */
        var depth = Math.min(1, C.dep + sp * C.dsp);
        splat(x, y, dx * C.force, dy * C.force, ink(depth), C.rad);
      }
    }
    P.x = x; P.y = y; P.has = true; lastMove = performance.now();
  }, {passive: true});
  addEventListener('pointerdown', function (e) {
    if (!booted) return;
    var r = canvas.getBoundingClientRect();
    var x = (e.clientX - r.left) / r.width, y = 1 - (e.clientY - r.top) / r.height;
    if (x < 0 || x > 1 || y < 0 || y > 1) return;
    var a = Math.random() * 6.283;
    splat(x, y, Math.cos(a) * C.force * 1.5, Math.sin(a) * C.force * 1.5,
      ink(Math.min(1, C.dep + .45)), C.rad * 1.3);
    lastMove = performance.now();
  }, {passive: true});

  /* ---------- 7. bucle, visibilidad y arranque ---------- */

  var raf = null, last = performance.now();
  var onScreen = true;
  function frame(now) {
    var dt = Math.min((now - last) / 1000, 1 / 30); last = now;
    ramp += (rampTarget - ramp) * Math.min(dt * 1.6, 1);
    canvas.style.opacity = ramp.toFixed(3);
    if (now - lastMove > 4200 && now - lastAuto > 3400) { burst(1, C.force * .22); lastAuto = now; }
    step(dt); render();
    raf = requestAnimationFrame(frame);
  }
  function play() {
    if (raf || !booted || !onScreen || document.hidden) return;
    last = performance.now(); raf = requestAnimationFrame(frame);
  }
  function pause() { if (raf) { cancelAnimationFrame(raf); raf = null; } }

  var style = document.createElement('style');
  style.id = 'mzink-style';
  style.textContent = 'canvas.mzink-canvas{position:absolute;inset:0;width:100%;height:100%;z-index:0;pointer-events:none;opacity:0}';
  document.head.appendChild(style);

  canvas.className = 'mzink-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  hero.prepend(canvas);

  size(); booted = true; applyMask(); burst(3, C.force * .25);
  play();

  var rt = null;
  addEventListener('resize', function () {
    clearTimeout(rt);
    rt = setTimeout(function () { size(); }, 150);
  });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) pause(); else play();
  });

  if (typeof IntersectionObserver === 'function') {
    new IntersectionObserver(function (entries) {
      onScreen = entries[0].isIntersecting;
      if (onScreen) play(); else pause();
    }, {threshold: 0}).observe(hero);
  }

  /* ---------- 8. dia / noche: body.dm-active ---------- */

  new MutationObserver(function () {
    var next = document.body.classList.contains('dm-active') ? 'night' : 'day';
    if (switching || next === mode) return;
    switching = true; rampTarget = 0;
    setTimeout(function () {
      mode = next;
      C = Object.assign({}, PRESET[mode]);
      initFBOs(); applyMask();
      rampTarget = 1; switching = false;
    }, 420);
  }).observe(document.body, {attributes: true, attributeFilter: ['class']});

})();
